import { useMemo } from 'react';
import type { ApiEndpoints, ProjectDataLLPEvent } from '@avisoma//shared';
import { Card } from '../../components/Card';
import { ExportAsCSVButton } from '../../components/ExportAsCSVButton';
import {
  extractionTableClass,
  extractionTbodyClass,
  extractionTdClass,
  extractionThClass,
  extractionTheadClass,
} from '../../components/reconstructedDocument/extractionTables/tableClasses';
import { PartNumberDisagreementWarning } from './PartNumberDisagreementWarning';

interface LLPStatusProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

type LLPPart = ProjectDataLLPEvent['parts'][number];

const DASH = '–';
const groupBorderClass = 'border-l border-gray-200 dark:border-zinc-700';

const formatCycles = (value: number | undefined) => (value == null ? DASH : value.toLocaleString('en-US'));

// Union of thrust labels across all parts, numeric-aware sort (5B3/3 < 5B4/3 < 7B22).
const collectThrusts = (parts: LLPPart[]) => {
  const thrusts = new Set<string>();
  parts.forEach((part) => part.engineVariants?.forEach((variant) => variant.thrust && thrusts.add(variant.thrust)));
  return Array.from(thrusts).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

// Per-part, per-thrust lookup of the matching engine variant (exact thrust match).
const variantAt = (part: LLPPart, thrust: string) => part.engineVariants?.find((variant) => variant.thrust === thrust);

// One CSV row per part: fixed prefix columns + per-thrust life-limit / operated / remaining triples.
const buildCsvRows = (parts: LLPPart[], thrusts: string[]) =>
  parts.map((part) => {
    const perThrust = thrusts.reduce<Record<string, number | undefined>>((acc, thrust) => {
      const variant = variantAt(part, thrust);
      acc[`lifeLimit ${thrust}`] = variant?.lifeLimitCycles;
      acc[`operated ${thrust}`] = variant?.csn;
      acc[`remaining ${thrust}`] = variant?.remainingCycles;
      return acc;
    }, {});
    return {
      name: part.name,
      partNumber: part.partNumber,
      serialNumber: part.serialNumber,
      tsn: part.tsn,
      csn: part.csn,
      ...perThrust,
    };
  });

const buildCsvColumns = (thrusts: string[]) => [
  { accessor: 'name', label: 'Part' },
  { accessor: 'partNumber', label: 'P/N' },
  { accessor: 'serialNumber', label: 'S/N' },
  { accessor: 'tsn', label: 'TSN' },
  { accessor: 'csn', label: 'CSN' },
  ...thrusts.flatMap((thrust) => [
    { accessor: `lifeLimit ${thrust}`, label: `Life Limit ${thrust}` },
    { accessor: `operated ${thrust}`, label: `Operated ${thrust}` },
    { accessor: `remaining ${thrust}`, label: `Remaining ${thrust}` },
  ]),
];

// Renders the three group cells (Life Limit / Operated / Remaining) for a single part row.
function GroupCells({ part, thrusts }: { part: LLPPart; thrusts: string[] }) {
  // No variants with a usable thrust label → one merged (colSpan) cell per group carrying the
  // part-level value, mirroring Oil's layout. Checking for thrust (not just array length) keeps
  // rows with label-less variants aligned with the header. `|| 1` covers the V===0 table where
  // each group is a single column.
  if (!part.engineVariants?.some((variant) => variant.thrust)) {
    const span = thrusts.length || 1;
    return (
      <>
        <td className={`${extractionTdClass} text-right ${groupBorderClass}`} colSpan={span}>
          {formatCycles(part.lifeLimitCycles)}
        </td>
        <td className={`${extractionTdClass} text-right ${groupBorderClass}`} colSpan={span}>
          {formatCycles(part.csn)}
        </td>
        <td className={`${extractionTdClass} text-right ${groupBorderClass}`} colSpan={span}>
          {formatCycles(part.remainingCycles)}
        </td>
      </>
    );
  }

  return (
    <>
      {thrusts.map((thrust, index) => (
        <td
          key={`ll-${thrust}`}
          className={`${extractionTdClass} text-right ${index === 0 ? groupBorderClass : ''}`}
        >
          {formatCycles(variantAt(part, thrust)?.lifeLimitCycles)}
        </td>
      ))}
      {thrusts.map((thrust, index) => (
        <td
          key={`op-${thrust}`}
          className={`${extractionTdClass} text-right ${index === 0 ? groupBorderClass : ''}`}
        >
          {formatCycles(variantAt(part, thrust)?.csn)}
        </td>
      ))}
      {thrusts.map((thrust, index) => (
        <td
          key={`rm-${thrust}`}
          className={`${extractionTdClass} text-right ${index === 0 ? groupBorderClass : ''}`}
        >
          {formatCycles(variantAt(part, thrust)?.remainingCycles)}
        </td>
      ))}
    </>
  );
}

export function LLPStatus({ response }: LLPStatusProps) {
  const parts = response.item.data?.llpEvents?.parts;
  const thrusts = useMemo(() => collectThrusts(parts ?? []), [parts]);

  if (!parts?.length) {
    return null;
  }

  const hasVariants = thrusts.length > 0;
  const groupHeaderClass = `${extractionThClass} text-center`;

  return (
    <Card
      header={
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">LLP Status</h2>
          <ExportAsCSVButton
            data={buildCsvRows(parts, thrusts)}
            columns={buildCsvColumns(thrusts)}
            title="LLP Status"
          />
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className={extractionTableClass}>
          <thead className={extractionTheadClass}>
            {hasVariants ? (
              <>
                <tr>
                  <th className={extractionThClass} rowSpan={2}>
                    Part
                  </th>
                  <th className={extractionThClass} rowSpan={2}>
                    P/N
                  </th>
                  <th className={extractionThClass} rowSpan={2}>
                    S/N
                  </th>
                  <th className={`${extractionThClass} text-right`} rowSpan={2}>
                    TSN
                  </th>
                  <th className={`${extractionThClass} text-right`} rowSpan={2}>
                    CSN
                  </th>
                  <th className={`${groupHeaderClass} ${groupBorderClass}`} colSpan={thrusts.length}>
                    Life Limit
                  </th>
                  <th className={`${groupHeaderClass} ${groupBorderClass}`} colSpan={thrusts.length}>
                    Operated
                  </th>
                  <th className={`${groupHeaderClass} ${groupBorderClass}`} colSpan={thrusts.length}>
                    Remaining
                  </th>
                </tr>
                <tr>
                  {thrusts.map((thrust, index) => (
                    <th
                      key={`ll-head-${thrust}`}
                      className={`${extractionThClass} text-right ${index === 0 ? groupBorderClass : ''}`}
                    >
                      {thrust}
                    </th>
                  ))}
                  {thrusts.map((thrust, index) => (
                    <th
                      key={`op-head-${thrust}`}
                      className={`${extractionThClass} text-right ${index === 0 ? groupBorderClass : ''}`}
                    >
                      {thrust}
                    </th>
                  ))}
                  {thrusts.map((thrust, index) => (
                    <th
                      key={`rm-head-${thrust}`}
                      className={`${extractionThClass} text-right ${index === 0 ? groupBorderClass : ''}`}
                    >
                      {thrust}
                    </th>
                  ))}
                </tr>
              </>
            ) : (
              <tr>
                <th className={extractionThClass}>Part</th>
                <th className={extractionThClass}>P/N</th>
                <th className={extractionThClass}>S/N</th>
                <th className={`${extractionThClass} text-right`}>TSN</th>
                <th className={`${extractionThClass} text-right`}>CSN</th>
                <th className={`${extractionThClass} text-right ${groupBorderClass}`}>Life Limit</th>
                <th className={`${extractionThClass} text-right ${groupBorderClass}`}>Operated</th>
                <th className={`${extractionThClass} text-right ${groupBorderClass}`}>Remaining</th>
              </tr>
            )}
          </thead>
          <tbody className={extractionTbodyClass}>
            {parts.map((part, index) => (
              <tr key={`${part.partNumber}-${part.serialNumber}-${index}`}>
                <td className={extractionTdClass}>{part.name || DASH}</td>
                <td className={`${extractionTdClass} font-mono`}>
                  <span className="inline-flex items-center gap-1">
                    {part.partNumber || DASH}
                    <PartNumberDisagreementWarning part={part} />
                  </span>
                </td>
                <td className={`${extractionTdClass} font-mono`}>{part.serialNumber || DASH}</td>
                <td className={`${extractionTdClass} text-right`}>{formatCycles(part.tsn)}</td>
                <td className={`${extractionTdClass} text-right`}>{formatCycles(part.csn)}</td>
                <GroupCells part={part} thrusts={thrusts} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
